import { InventoryItemOutcome } from "@redotech/crdb-enums/inventory/inventory-item-outcome";
import { accountIdFormat } from "@redotech/redo-account-id";
import {
  FlushPickStrategySchema,
  FlushReasonSchema,
} from "@redotech/redo-inventory-common/flush";
import { BinFlushBlockReason } from "@redotech/redo-inventory-common/flush-bin";
import {
  inventoryItemIdFormat,
  locationIdFormat,
} from "@redotech/redo-inventory-id";
import { productIdFormat } from "@redotech/redo-product-id";
import {
  storageLocationIdFormat,
  transferSessionIdFormat,
} from "@redotech/redo-warehouse-id";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

// ─── shared types ────────────────────────────────────────────────

const FlushSessionIdSchema = transferSessionIdFormat.schema;

const ResolvedFlushProductSchema = z.object({
  productId: productIdFormat.schema,
  productTitle: z.string(),
  sku: z.string().nullable(),
  barcode: z.string().nullable(),
});

const FlushScanErrorSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("unknown_code") }),
  z.object({
    kind: z.literal("not_at_warehouse"),
    product: ResolvedFlushProductSchema,
  }),
  z.object({
    kind: z.literal("outcome_mismatch"),
    product: ResolvedFlushProductSchema,
    outcome: createEnum(InventoryItemOutcome).nullable(),
  }),
  // The merchant requires strict license plate scans and a SKU, UPC, or
  // barcode was scanned instead of the inventory item.
  z.object({
    kind: z.literal("license_plate_required"),
    product: ResolvedFlushProductSchema,
  }),
  // A bin was scanned but not every unit in it can be flushed for the reason.
  z.object({
    kind: z.literal("bin_not_flushable"),
    binName: z.string(),
    reason: createEnum(BinFlushBlockReason),
    unitCount: z.number().int(),
  }),
]);

// A bin whose units all qualify; `flushBin` empties it once confirmed.
const BinFlushPreviewSchema = z.object({
  storageLocationId: storageLocationIdFormat.schema,
  binName: z.string(),
  unitCount: z.number().int(),
});

const FlushSessionTotalsSchema = z.object({
  scanCount: z.number().int(),
  skuCount: z.number().int(),
});

const FlushScanSchema = z.object({
  sltaId: z.string(),
  productTitle: z.string(),
  sku: z.string().nullable(),
  sourceBinName: z.string(),
  quantity: z.number().int(),
  scannedAt: z.date(),
});

const PickLineSchema = z.object({
  id: z.string(),
  productId: productIdFormat.schema,
  productTitle: z.string(),
  sku: z.string().nullable(),
  // The unit to scan when the merchant requires strict license plate scans.
  inventoryItemId: inventoryItemIdFormat.schema.nullable(),
  imageUrl: z.string().nullable(),
  sourceBinName: z.string(),
  expectedQuantity: z.number().int(),
  pickedQuantity: z.number().int(),
  // Units the operator couldn't find in the bin, moved to Lost.
  lostQuantity: z.number().int(),
});

// ─── endpoints ───────────────────────────────────────────────────

export const startFlushSession = {
  name: "startFlushSession" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    reason: FlushReasonSchema,
    note: z.string().max(500).nullish(),
    pickStrategy: FlushPickStrategySchema,
    // Null takes every managed merchant's items; liquidations only.
    merchantAccountId: accountIdFormat.schema.nullable(),
  }),
  output: z.object({ flushSessionId: FlushSessionIdSchema }),
};

export const scanFlushItem = {
  name: "scanFlushItem" as const,
  input: z.object({
    flushSessionId: FlushSessionIdSchema,
    code: z.string().min(1),
  }),
  output: z.object({
    resolvedProduct: ResolvedFlushProductSchema.nullable(),
    sourceBinName: z.string().nullable(),
    flushBinName: z.string(),
    totals: FlushSessionTotalsSchema,
    error: FlushScanErrorSchema.nullable(),
    matchedPickLineId: z.string().nullable(),
    binPreview: BinFlushPreviewSchema.nullable(),
  }),
};

export const flushBin = {
  name: "flushBin" as const,
  input: z.object({
    flushSessionId: FlushSessionIdSchema,
    storageLocationId: storageLocationIdFormat.schema,
  }),
  output: z.object({
    error: FlushScanErrorSchema.nullable(),
    unitCount: z.number().int(),
  }),
};

export const markFlushLineLost = {
  name: "markFlushLineLost" as const,
  input: z.object({
    flushSessionId: FlushSessionIdSchema,
    pickLineId: z.string(),
  }),
  output: z.object({ lostQuantity: z.number().int() }),
};

export const finalizeFlushSession = {
  name: "finalizeFlushSession" as const,
  input: z.object({ flushSessionId: FlushSessionIdSchema }),
  output: z.object({ completedAt: z.date() }),
};

export const cancelFlushSession = {
  name: "cancelFlushSession" as const,
  input: z.object({ flushSessionId: FlushSessionIdSchema }),
  output: z.object({ canceledAt: z.date(), reversedCount: z.number().int() }),
};

export const getFlushSession = {
  name: "getFlushSession" as const,
  input: z.object({ flushSessionId: FlushSessionIdSchema }),
  output: z.object({
    session: z.object({
      flushSessionId: FlushSessionIdSchema,
      locationId: locationIdFormat.schema,
      reason: FlushReasonSchema,
      pickStrategy: FlushPickStrategySchema,
      flushBinName: z.string(),
      note: z.string().nullable(),
      operatorName: z.string().nullable(),
      // Null when the session takes every merchant's items or has no name.
      merchantName: z.string().nullable(),
      allMerchants: z.boolean(),
      createdAt: z.date(),
      completedAt: z.date().nullable(),
      canceledAt: z.date().nullable(),
      // Only an inventory item scan is accepted, not a SKU, UPC, or barcode.
      licensePlateRequired: z.boolean(),
    }),
    scans: z.array(FlushScanSchema),
    pickLines: z.array(PickLineSchema),
    totals: FlushSessionTotalsSchema,
  }),
};

export const getActiveFlushSession = {
  name: "getActiveFlushSession" as const,
  input: z.object({ locationId: locationIdFormat.schema }),
  output: z.object({
    session: z
      .object({
        flushSessionId: FlushSessionIdSchema,
        reason: FlushReasonSchema,
        createdAt: z.date(),
      })
      .nullable(),
  }),
};

export const listFlushMerchants = {
  name: "listFlushMerchants" as const,
  input: z.object({}),
  output: z.object({
    merchants: z.array(
      z.object({ accountId: accountIdFormat.schema, name: z.string() }),
    ),
  }),
};

export const listSuggestedFlushes = {
  name: "listSuggestedFlushes" as const,
  input: z.object({ locationId: locationIdFormat.schema }),
  output: z.object({
    suggestions: z.array(
      z.object({
        merchantAccountId: accountIdFormat.schema,
        merchantName: z.string().nullable(),
        reason: FlushReasonSchema,
        itemCount: z.number().int(),
        unitCount: z.number().int(),
        oldestItemCreatedAt: z.date(),
        // Set while any operator has an open session for this merchant and
        // reason; starting another is rejected.
        inProgress: z
          .object({ operatorName: z.string().nullable() })
          .nullable(),
      }),
    ),
  }),
};
